import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { 
  MagnifyingGlass, 
  Download, 
  Funnel,
  Eye,
  Warning,
  CheckCircle,
  Envelope,
  Buildings,
  Users,
  Briefcase,
  Receipt,
  CurrencyDollar,
  CalendarBlank,
  Info,
  Trash,
  X,
  CheckSquare,
  Square,
  Prohibit,
  User,
  Spinner,
  Archive
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Rule badge colors
const RULE_COLORS = {
  A: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  B: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  C: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

// Action badge colors
const ACTION_COLORS = {
  delete: "bg-red-500/20 text-red-400 border-red-500/30",
  archive: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  blocked: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
};

const RULE_LABELS = {
  A: "Test/Demo Name",
  B: "Low Data",
  C: "Internal Email",
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Determine action for a tenant based on business objects
function getTenantAction(tenant) {
  const hasBusinessObjects = tenant.jobs > 0 || tenant.visits > 0 || tenant.invoices > 0 || tenant.payments > 0;
  if (hasBusinessObjects) {
    return { action: "archive", label: "Archive", color: ACTION_COLORS.archive };
  }
  return { action: "delete", label: "Delete", color: ACTION_COLORS.delete };
}

export default function AdminLaunchCleanup() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filters
  const [includeGroupA, setIncludeGroupA] = useState(true);
  const [includeGroupB, setIncludeGroupB] = useState(true);
  const [includeGroupC, setIncludeGroupC] = useState(true);
  const [lowDataThreshold, setLowDataThreshold] = useState(1);
  
  // Search
  const [search, setSearch] = useState("");
  
  // Rules info
  const [showRules, setShowRules] = useState(false);
  const [rules, setRules] = useState(null);
  
  // Selection state
  const [selectedTenants, setSelectedTenants] = useState(new Set());
  
  // Deletion state
  const [showDryRun, setShowDryRun] = useState(false);
  const [dryRunData, setDryRunData] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        include_group_a: includeGroupA,
        include_group_b: includeGroupB,
        include_group_c: includeGroupC,
        low_data_threshold: lowDataThreshold,
      });
      
      const response = await fetch(
        `${API}/admin/launch-cleanup/eligible-tenants?${params}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        // Clear selections when data changes
        setSelectedTenants(new Set());
      } else {
        const err = await response.json();
        setError(err.detail || "Failed to fetch data");
      }
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [includeGroupA, includeGroupB, includeGroupC, lowDataThreshold]);

  const fetchRules = async () => {
    try {
      const response = await fetch(`${API}/admin/launch-cleanup/rules`, {
        credentials: "include",
      });
      if (response.ok) {
        const result = await response.json();
        setRules(result);
      }
    } catch (err) {
      console.error("Failed to fetch rules:", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRules();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    const params = new URLSearchParams({
      include_group_a: includeGroupA,
      include_group_b: includeGroupB,
      include_group_c: includeGroupC,
      low_data_threshold: lowDataThreshold,
    });
    
    window.open(
      `${API}/admin/launch-cleanup/export-csv?${params}`,
      "_blank"
    );
  };

  // Filter tenants by search
  const filteredTenants = data?.tenants?.filter((t) => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      t.company_name?.toLowerCase().includes(searchLower) ||
      t.tenant_id?.toLowerCase().includes(searchLower) ||
      t.owner_email?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Selection handlers
  const toggleTenant = (tenantId) => {
    const newSelected = new Set(selectedTenants);
    if (newSelected.has(tenantId)) {
      newSelected.delete(tenantId);
    } else {
      newSelected.add(tenantId);
    }
    setSelectedTenants(newSelected);
  };

  const selectAllFiltered = () => {
    const newSelected = new Set(filteredTenants.map(t => t.tenant_id));
    setSelectedTenants(newSelected);
  };

  const clearSelection = () => {
    setSelectedTenants(new Set());
  };

  // Dry run
  const executeDryRun = async () => {
    if (selectedTenants.size === 0) return;
    
    try {
      setDryRunLoading(true);
      setDryRunData(null);
      
      const response = await fetch(`${API}/admin/launch-cleanup/dry-run`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_ids: Array.from(selectedTenants)
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setDryRunData(result);
        setShowDryRun(true);
      } else {
        const err = await response.json();
        setError(err.detail || "Dry run failed");
      }
    } catch (err) {
      setError("Dry run failed");
    } finally {
      setDryRunLoading(false);
    }
  };

  // Execute deletion
  const executeDelete = async () => {
    if (confirmText !== "CLEAN LAUNCH") return;
    
    // Get ALL tenant IDs - both to_delete and to_archive
    const deleteIds = dryRunData?.tenants_to_delete?.map(t => t.tenant_id) || [];
    const archiveIds = dryRunData?.tenants_to_archive?.map(t => t.tenant_id) || [];
    const allEligibleIds = [...deleteIds, ...archiveIds];
    if (allEligibleIds.length === 0) return;
    
    try {
      setDeleteLoading(true);
      
      const response = await fetch(`${API}/admin/launch-cleanup/delete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_ids: allEligibleIds,
          confirm: "CLEAN LAUNCH"
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setDeleteResult(result);
        setShowDeleteConfirm(false);
        setShowDryRun(false);
        setConfirmText("");
        setSelectedTenants(new Set());
        // Refresh data
        fetchData();
      } else {
        const err = await response.json();
        setError(err.detail || err.error || "Deletion failed");
      }
    } catch (err) {
      setError("Deletion failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Launch Cleanup</h1>
            <p className="text-zinc-400 mt-1">
              Review and delete demo/test tenants before launch.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowRules(!showRules)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Info size={16} className="mr-2" />
              {showRules ? "Hide Rules" : "View Rules"}
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={!data?.tenants?.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="export-csv-btn"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Rules Panel */}
        {showRules && rules && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Eye size={18} />
              Eligibility Rules
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={RULE_COLORS.A}>A</Badge>
                  <span className="font-medium text-purple-400">{rules.group_a.name}</span>
                </div>
                <p className="text-sm text-zinc-400">{rules.group_a.description}</p>
              </div>
              
              <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={RULE_COLORS.B}>B</Badge>
                  <span className="font-medium text-amber-400">{rules.group_b.name}</span>
                </div>
                <p className="text-sm text-zinc-400">{rules.group_b.description}</p>
              </div>
              
              <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={RULE_COLORS.C}>C</Badge>
                  <span className="font-medium text-blue-400">{rules.group_c.name}</span>
                </div>
                <p className="text-sm text-zinc-400">{rules.group_c.description}</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-sm">
              <p className="text-red-400 font-medium mb-2">Hard Blocks (Cannot Delete):</p>
              <ul className="text-zinc-400 list-disc list-inside">
                <li>Tenants with invoices or payments</li>
                <li>Tenants with jobs or visits</li>
                <li>Platform admin owned tenants</li>
                <li>Multi-tenant users are preserved</li>
              </ul>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-white">{data.total_eligible}</div>
              <div className="text-sm text-zinc-500">Total Flagged</div>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">{data.summary.group_a_test_demo}</div>
              <div className="text-sm text-zinc-500">Test/Demo (A)</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-amber-400">{data.summary.group_b_low_data}</div>
              <div className="text-sm text-zinc-500">Low Data (B)</div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-blue-400">{data.summary.group_c_internal_email}</div>
              <div className="text-sm text-zinc-500">Internal (C)</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-400">{selectedTenants.size}</div>
              <div className="text-sm text-zinc-500">Selected</div>
            </div>
          </div>
        )}

        {/* Filters & Selection Controls */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={18} className="text-zinc-400" />
            <span className="font-medium">Filters & Selection</span>
          </div>
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Group toggles */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeGroupA}
                onChange={(e) => setIncludeGroupA(e.target.checked)}
                className="w-4 h-4 rounded bg-zinc-800 border-zinc-600"
              />
              <Badge className={RULE_COLORS.A}>A</Badge>
              <span className="text-sm text-zinc-300">Test/Demo</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeGroupB}
                onChange={(e) => setIncludeGroupB(e.target.checked)}
                className="w-4 h-4 rounded bg-zinc-800 border-zinc-600"
              />
              <Badge className={RULE_COLORS.B}>B</Badge>
              <span className="text-sm text-zinc-300">Low Data</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeGroupC}
                onChange={(e) => setIncludeGroupC(e.target.checked)}
                className="w-4 h-4 rounded bg-zinc-800 border-zinc-600"
              />
              <Badge className={RULE_COLORS.C}>C</Badge>
              <span className="text-sm text-zinc-300">Internal</span>
            </label>
            
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-zinc-700">
              <span className="text-sm text-zinc-400">Threshold:</span>
              <select
                value={lowDataThreshold}
                onChange={(e) => setLowDataThreshold(Number(e.target.value))}
                className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
              >
                <option value={0}>0 customers</option>
                <option value={1}>≤1 customer</option>
                <option value={2}>≤2 customers</option>
              </select>
            </div>
            
            {/* Selection buttons */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-zinc-700">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllFiltered}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <CheckSquare size={14} className="mr-1" />
                Select All ({filteredTenants.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearSelection}
                disabled={selectedTenants.size === 0}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Square size={14} className="mr-1" />
                Clear
              </Button>
            </div>
            
            {/* Search */}
            <div className="flex-1 min-w-[200px] ml-4 pl-4 border-l border-zinc-700">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        {selectedTenants.size > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye size={20} className="text-amber-400" />
              <div>
                <p className="font-medium text-amber-400">{selectedTenants.size} tenant(s) selected</p>
                <p className="text-sm text-zinc-400">Preview will show: Delete (empty) or Archive (has data)</p>
              </div>
            </div>
            <Button
              onClick={executeDryRun}
              disabled={dryRunLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="dry-run-btn"
            >
              {dryRunLoading ? (
                <>
                  <Spinner size={16} className="mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Eye size={16} className="mr-2" />
                  Dry Run Preview
                </>
              )}
            </Button>
          </div>
        )}

        {/* Delete Result */}
        {deleteResult && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CheckCircle size={24} className="text-emerald-400" />
                <div>
                  <p className="font-medium text-emerald-400">Cleanup Complete</p>
                  <p className="text-sm text-zinc-400">
                    {deleteResult.summary.total_deleted} deleted, {deleteResult.summary.total_archived} archived, {deleteResult.summary.total_skipped} skipped
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteResult(null)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <X size={14} className="mr-1" />
                Dismiss
              </Button>
            </div>
            
            {deleteResult.deleted?.length > 0 && (
              <div className="bg-zinc-900 rounded p-3 mb-2">
                <p className="text-sm text-red-400 mb-2">Deleted ({deleteResult.deleted.length}):</p>
                <div className="text-xs text-zinc-400 max-h-32 overflow-y-auto">
                  {deleteResult.deleted.map(d => (
                    <div key={d.tenant_id} className="mb-1">
                      • {d.company_name} ({d.tenant_id.slice(0, 16)}...)
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {deleteResult.archived?.length > 0 && (
              <div className="bg-zinc-900 rounded p-3 mb-2">
                <p className="text-sm text-amber-400 mb-2">Archived ({deleteResult.archived.length}):</p>
                <div className="text-xs text-zinc-400 max-h-32 overflow-y-auto">
                  {deleteResult.archived.map(a => (
                    <div key={a.tenant_id} className="mb-1">
                      • {a.company_name} ({a.tenant_id.slice(0, 16)}...) - {a.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {deleteResult.skipped?.length > 0 && (
              <div className="bg-zinc-900 rounded p-3">
                <p className="text-sm text-zinc-400 mb-2">Skipped ({deleteResult.skipped.length}):</p>
                <div className="text-xs text-zinc-400 max-h-32 overflow-y-auto">
                  {deleteResult.skipped.map(s => (
                    <div key={s.tenant_id} className="mb-1">
                      • {s.company_name || s.tenant_id}: {s.reasons?.join(", ") || s.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 flex items-center gap-2">
            <Warning size={20} />
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto text-zinc-400 hover:text-white"
            >
              <X size={14} />
            </Button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-zinc-600 border-t-blue-500 rounded-full mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading eligible tenants...</p>
          </div>
        )}

        {/* Table */}
        {!loading && data && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-800/50 border-b border-zinc-700">
                    <th className="w-10 px-4 py-3"></th>
                    <th className="text-left px-4 py-3 font-medium text-zinc-400">
                      <div className="flex items-center gap-2">
                        <Buildings size={14} />
                        Company
                      </div>
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-zinc-400">Tenant ID</th>
                    <th className="text-left px-4 py-3 font-medium text-zinc-400">
                      <div className="flex items-center gap-2">
                        <Envelope size={14} />
                        Owner
                      </div>
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-zinc-400">
                      <div className="flex items-center gap-2">
                        <CalendarBlank size={14} />
                        Created
                      </div>
                    </th>
                    <th className="text-center px-4 py-3 font-medium text-zinc-400">Cust</th>
                    <th className="text-center px-4 py-3 font-medium text-zinc-400">Jobs</th>
                    <th className="text-center px-4 py-3 font-medium text-zinc-400">Inv</th>
                    <th className="text-center px-4 py-3 font-medium text-zinc-400">Pay</th>
                    <th className="text-center px-4 py-3 font-medium text-zinc-400">Visits</th>
                    <th className="text-left px-4 py-3 font-medium text-zinc-400">Rules</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-zinc-500">
                        <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
                        <p>No tenants match the current filters</p>
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((tenant) => {
                      const actionInfo = getTenantAction(tenant);
                      const isSelected = selectedTenants.has(tenant.tenant_id);
                      const isBlocked = actionInfo.action === "archive"; // tenant with business data
                      
                      return (
                        <tr
                          key={tenant.tenant_id}
                          className={`border-b border-zinc-800 hover:bg-zinc-800/30 ${
                            isSelected ? (actionInfo.action === "delete" ? "bg-red-500/5" : "bg-amber-500/5") : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleTenant(tenant.tenant_id)}
                              className={`w-5 h-5 rounded border flex items-center justify-center ${
                                isSelected 
                                  ? (actionInfo.action === "delete" ? "bg-red-500 border-red-500" : "bg-amber-500 border-amber-500")
                                  : "bg-zinc-800 border-zinc-600 hover:border-zinc-500"
                              }`}
                              data-testid={`select-${tenant.tenant_id}`}
                            >
                              {isSelected && <CheckCircle size={14} className="text-white" weight="bold" />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-white truncate max-w-[180px]" title={tenant.company_name}>
                              {tenant.company_name || "-"}
                            </div>
                            {tenant.status !== "active" && (
                              <Badge variant="outline" className="text-xs mt-1 bg-zinc-700/50 text-zinc-400 border-zinc-600">
                                {tenant.status}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-xs text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">
                              {tenant.tenant_id.slice(0, 14)}...
                            </code>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-zinc-300 truncate max-w-[160px]" title={tenant.owner_email}>
                              {tenant.owner_email || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-zinc-400 text-xs">
                            {formatDate(tenant.created_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={tenant.customers <= 1 ? "text-amber-400" : "text-zinc-300"}>
                              {tenant.customers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={tenant.jobs > 0 ? "text-red-400 font-medium" : "text-zinc-300"}>
                              {tenant.jobs}
                              {tenant.jobs > 0 && <Prohibit size={12} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={tenant.invoices > 0 ? "text-red-400 font-medium" : "text-zinc-300"}>
                              {tenant.invoices}
                              {tenant.invoices > 0 && <Prohibit size={12} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={tenant.payments > 0 ? "text-red-400 font-medium" : "text-zinc-300"}>
                              {tenant.payments}
                              {tenant.payments > 0 && <Prohibit size={12} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={tenant.visits > 0 ? "text-red-400 font-medium" : "text-zinc-300"}>
                              {tenant.visits}
                              {tenant.visits > 0 && <Prohibit size={12} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {tenant.matched_rules.map((rule) => (
                                <Badge
                                  key={rule}
                                  className={`${RULE_COLORS[rule]} text-xs`}
                                  title={RULE_LABELS[rule]}
                                >
                                  {rule}
                                </Badge>
                              ))}
                              {isBlocked && (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                                  BLOCKED
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {filteredTenants.length > 0 && (
              <div className="px-4 py-3 bg-zinc-800/30 border-t border-zinc-700 text-sm text-zinc-500">
                Showing {filteredTenants.length} of {data.total_eligible} flagged tenants
              </div>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <span className="text-zinc-500">Legend:</span>
            <div className="flex items-center gap-2">
              <Badge className={RULE_COLORS.A}>A</Badge>
              <span className="text-zinc-400">Test/Demo name</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={RULE_COLORS.B}>B</Badge>
              <span className="text-zinc-400">Low data</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={RULE_COLORS.C}>C</Badge>
              <span className="text-zinc-400">Internal email</span>
            </div>
            <div className="flex items-center gap-2">
              <Prohibit size={14} className="text-red-400" />
              <span className="text-zinc-400">Blocked (has data)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dry Run Modal */}
      {showDryRun && dryRunData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye size={24} className="text-amber-400" />
                <div>
                  <h2 className="text-lg font-semibold text-white">Dry Run Preview</h2>
                  <p className="text-sm text-zinc-400">
                    Review what will be deleted before confirming
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDryRun(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X size={20} />
              </Button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{dryRunData.summary.total_selected}</div>
                  <div className="text-sm text-zinc-500">Selected</div>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-400">{dryRunData.summary.total_to_delete}</div>
                  <div className="text-sm text-zinc-500">To Delete</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-400">{dryRunData.summary.total_to_archive}</div>
                  <div className="text-sm text-zinc-500">To Archive</div>
                </div>
                <div className="bg-zinc-700/50 border border-zinc-600 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-zinc-400">{dryRunData.summary.total_blocked}</div>
                  <div className="text-sm text-zinc-500">Blocked</div>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{dryRunData.summary.total_documents_to_delete}</div>
                  <div className="text-sm text-zinc-500">Documents</div>
                </div>
              </div>

              {/* User Impact */}
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                  <User size={18} />
                  User Impact
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-red-500/10 rounded p-3">
                    <div className="text-red-400 font-medium">{dryRunData.summary.total_users_to_delete} users to DELETE</div>
                    <p className="text-zinc-500 text-xs">Single-tenant, non-admin users</p>
                  </div>
                  <div className="bg-emerald-500/10 rounded p-3">
                    <div className="text-emerald-400 font-medium">{dryRunData.summary.total_users_to_preserve} users PRESERVED</div>
                    <p className="text-zinc-500 text-xs">Multi-tenant or admin users</p>
                  </div>
                </div>
              </div>

              {/* Blocked Tenants */}
              {dryRunData.blocked_tenants.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="font-medium text-red-400 mb-3 flex items-center gap-2">
                    <Prohibit size={18} />
                    Blocked Tenants ({dryRunData.blocked_tenants.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {dryRunData.blocked_tenants.map(t => (
                      <div key={t.tenant_id} className="bg-zinc-900 rounded p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white font-medium">{t.company_name}</span>
                            <span className="text-zinc-500 text-xs ml-2">{t.owner_email}</span>
                          </div>
                        </div>
                        <div className="text-xs text-red-400 mt-1">
                          {t.block_reasons?.join(" | ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tenants to Delete (empty) */}
              {dryRunData.tenants_to_delete?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="font-medium text-red-400 mb-3 flex items-center gap-2">
                    <Trash size={18} />
                    To Delete ({dryRunData.tenants_to_delete.length}) - Empty Tenants
                  </h3>
                  <p className="text-xs text-zinc-500 mb-3">These tenants have NO business objects and will be permanently deleted.</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {dryRunData.tenants_to_delete.map(t => (
                      <div key={t.tenant_id} className="bg-zinc-900 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-white font-medium">{t.company_name}</span>
                            <span className="text-zinc-500 text-xs ml-2">{t.owner_email}</span>
                          </div>
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">DELETE</Badge>
                        </div>
                        
                        {/* Counts breakdown */}
                        <div className="flex flex-wrap gap-2 text-xs">
                          {Object.entries(t.counts || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
                            <span key={k} className="bg-zinc-800 px-2 py-0.5 rounded text-zinc-400">
                              {k}: {v}
                            </span>
                          ))}
                        </div>
                        
                        {/* Users */}
                        {(t.users_to_delete?.length > 0 || t.users_to_preserve?.length > 0) && (
                          <div className="mt-2 pt-2 border-t border-zinc-800 text-xs">
                            {t.users_to_delete?.map(u => (
                              <div key={u.user_id} className="text-red-400">
                                • {u.email} - {u.reason}
                              </div>
                            ))}
                            {t.users_to_preserve?.map(u => (
                              <div key={u.user_id} className="text-emerald-400">
                                • {u.email} - {u.reason}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tenants to Archive (has data) */}
              {dryRunData.tenants_to_archive?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h3 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                    <Archive size={18} />
                    To Archive ({dryRunData.tenants_to_archive.length}) - Has Business Data
                  </h3>
                  <p className="text-xs text-zinc-500 mb-3">These tenants have jobs/invoices/etc. Status will be set to ARCHIVED (login blocked, data preserved).</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {dryRunData.tenants_to_archive.map(t => (
                      <div key={t.tenant_id} className="bg-zinc-900 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-white font-medium">{t.company_name}</span>
                            <span className="text-zinc-500 text-xs ml-2">{t.owner_email}</span>
                          </div>
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">ARCHIVE</Badge>
                        </div>
                        
                        {/* Business object counts */}
                        <div className="flex flex-wrap gap-2 text-xs mb-2">
                          <span className="bg-amber-500/10 px-2 py-0.5 rounded text-amber-400">
                            jobs: {t.business_object_counts?.jobs || 0}
                          </span>
                          <span className="bg-amber-500/10 px-2 py-0.5 rounded text-amber-400">
                            visits: {t.business_object_counts?.visits || 0}
                          </span>
                          <span className="bg-amber-500/10 px-2 py-0.5 rounded text-amber-400">
                            invoices: {t.business_object_counts?.invoices || 0}
                          </span>
                          <span className="bg-amber-500/10 px-2 py-0.5 rounded text-amber-400">
                            payments: {t.business_object_counts?.payments || 0}
                          </span>
                        </div>
                        
                        {/* Warnings */}
                        {t.warnings?.length > 0 && (
                          <div className="text-xs text-amber-400">
                            {t.warnings.map((w, i) => (
                              <div key={i}>⚠ {w}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Collection Totals */}
              {Object.keys(dryRunData.summary.collection_totals || {}).length > 0 && (
                <div className="bg-zinc-800 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-3">Documents to Delete by Collection</h3>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    {Object.entries(dryRunData.summary.collection_totals)
                      .filter(([_, v]) => v > 0)
                      .map(([k, v]) => (
                        <div key={k} className="bg-zinc-900 rounded px-3 py-2">
                          <span className="text-zinc-400">{k}:</span>
                          <span className="text-white ml-2 font-medium">{v}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-zinc-700 flex items-center justify-between bg-zinc-800/50">
              <Button
                variant="outline"
                onClick={() => setShowDryRun(false)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              
              {(dryRunData.summary.total_to_delete > 0 || dryRunData.summary.total_to_archive > 0) ? (
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="proceed-delete-btn"
                >
                  <Trash size={16} className="mr-2" />
                  Proceed ({dryRunData.summary.total_to_delete} delete, {dryRunData.summary.total_to_archive} archive)
                </Button>
              ) : (
                <div className="text-zinc-500">No eligible tenants</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && dryRunData && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 border border-red-500/50 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash size={24} className="text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Confirm Deletion</h2>
                <p className="text-sm text-red-400">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">{dryRunData.summary.total_to_delete}</div>
                  <div className="text-xs text-zinc-400">to DELETE permanently</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400">{dryRunData.summary.total_to_archive}</div>
                  <div className="text-xs text-zinc-400">to ARCHIVE</div>
                </div>
              </div>
              <div className="text-xs text-zinc-500 text-center">
                {dryRunData.summary.total_documents_to_delete} documents will be deleted | {dryRunData.summary.total_users_to_delete} users affected
              </div>
            </div>
            
            <div className="mb-6">
              <label className="text-sm text-zinc-400 block mb-2">
                Type <span className="font-mono bg-zinc-800 px-2 py-0.5 rounded text-red-400">CLEAN LAUNCH</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CLEAN LAUNCH"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-4 py-3 text-white text-center font-mono focus:outline-none focus:border-red-500"
                data-testid="confirm-delete-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConfirmText("");
                }}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={executeDelete}
                disabled={confirmText !== "CLEAN LAUNCH" || deleteLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="execute-delete-btn"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size={16} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Delete Forever
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
